<template>
  <fragment>
    <thead>
      <tr>
        <th
          v-for="col in displayColumns"
          :key="col.id"
          @click="sortTable(col.id)"
        >
          {{ col.display }}
          <font-awesome-icon
            :icon="['fas', 'sort']"
            class="icon alt"
          />
          <span
            v-if="col.id == sortColumn"
            :class="ascending ? 'active-down' : 'active-up'"
          >
            <font-awesome-icon
              :icon="['fas', 'sort-down']"
              class="icon alt"
            />
            <font-awesome-icon
              :icon="['fas', 'sort-up']"
              class="icon alt"
            />
          </span>
        </th>
        <th
          v-if="$store.getters.isKgbAdminUser"
          class="text-right actions"
        >
          <span class="sr-only">Actions</span>
        </th>
      </tr>
    </thead>
    <tbody>
      <tr
        v-for="reportRecord in filteredReportData"
        :key="reportRecord.id"
      >
        <td
          v-for="col in displayColumns"
          :key="reportRecord.id + '-' + col.id"
          :style="col.style"
          :title="
            col.title ? col.title(reportRecord[col.id], reportRecord) : null
          "
          :class="col.class"
        >
          <span
            v-if="
              col.isDownloadLink &&
                reportRecord[col.id] &&
                !reportRecord['fileIsDeleted'] &&
                reportRecord['fileCanBeDownloaded']
            "
            class="link"
            @click="
              downloadFile(reportRecord['deviceId'], reportRecord[col.id])
            "
          >
            {{
              col.format
                ? col.format(reportRecord[col.id])
                : reportRecord[col.id]
            }}
          </span>
          <span
            v-else-if="
              col.isDownloadLink &&
                reportRecord[col.id] &&
                reportRecord['fileIsDeleted']
            "
            v-b-popover.hover="buildDeletedByText(reportRecord)"
            class="deleted-file"
          >
            {{
              col.format
                ? col.format(reportRecord[col.id])
                : reportRecord[col.id]
            }}
          </span>
          <span v-else-if="col.isBulletedList && reportRecord[col.id]">
            <ul class="margin-0">
              <li
                v-for="listText in reportRecord[col.id]"
                :key="listText"
              >
                {{ listText }}
              </li>
            </ul>
          </span>
          <span v-else>
            {{
              col.format
                ? col.format(reportRecord[col.id])
                : reportRecord[col.id]
            }}
          </span>
          <label class="d-block d-md-none">{{ col.display }}</label>
        </td>
        <td
          v-if="$store.getters.isKgbAdminUser"
          class="actions"
        >
          <button
            v-if="$store.getters.isKgbAdminUser"
            v-b-tooltip.hover
            type="button"
            class="blue-hover"
            title="History"
            style="margin-right: 3px;"
            @click="goToHistory(reportRecord)"
          >
            <font-awesome-icon
              :icon="['fa', 'book']"
              class="icon alt"
            />
          </button>
          <button
            v-if="$store.getters.isKgbAdminUser"
            v-b-tooltip.hover
            type="button"
            class="blue-hover"
            title="Edit"
            style="margin-right: 3px;"
            @click="goToEdit(reportRecord)"
          >
            <font-awesome-icon
              :icon="['fas', 'pencil']"
              class="icon alt"
            />
          </button>
        </td>
      </tr>
    </tbody>
    <tfoot v-if="reportData.length > recordsPerPage">
      <tr>
        <td :colspan="displayColumns.length">
          <b-pagination
            v-model="currentPage"
            :total-rows="reportData.length"
            :per-page="recordsPerPage"
            @change="pageChange"
          />
        </td>
      </tr>
    </tfoot>

    <b-modal
      id="usage-history-modal"
      ref="usageHistoryModal"
      :title="
        `Usage History for
        ${changeLogUsage.serialNumber}
        at ${new Date(changeLogUsage.startTime).toLocaleString()}`
      "
      ok-only
      ok-title="Close"
      size="lg"
    >
      <div>
        <div v-if="loadingChangeLog">
          <Loader />
        </div>
        <div v-if="!loadingChangeLog && changeLogData.length === 0">
          No changes made to this usage record
        </div>
        <responsive-table v-if="changeLogData.length > 0 && !loadingChangeLog">
          <thead class="inline-header">
            <tr>
              <th>
                Changes
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="changeLog in changeLogData"
              :key="changeLog.id"
            >
              <td>
                {{ changeLog.reason_for_update }}
                <span style="font-style: italic;">
                  - {{ changeLog.created_by_name }} at
                  {{ new Date(changeLog.created).toLocaleString() }}
                </span>
                <table class="table">
                  <tr>
                    <th style="width: 36%">
                      Field
                    </th>
                    <th style="width: 32%">
                      Previous
                    </th>
                    <th style="width: 32%">
                      New
                    </th>
                  </tr>
                  <tbody>
                    <tr
                      v-if="
                        changeLog.old_aircraft_tail_number !==
                          changeLog.new_aircraft_tail_number
                      "
                    >
                      <td>Tail Number</td>
                      <td>{{ changeLog.old_aircraft_tail_number }}</td>
                      <td>{{ changeLog.new_aircraft_tail_number }}</td>
                    </tr>
                    <tr
                      v-if="
                        changeLog.old_recorder_part_number !==
                          changeLog.new_recorder_part_number
                      "
                    >
                      <td>Recorder Part Number</td>
                      <td>{{ changeLog.old_recorder_part_number }}</td>
                      <td>{{ changeLog.new_recorder_part_number }}</td>
                    </tr>
                    <tr
                      v-if="
                        changeLog.old_recorder_serial_number !==
                          changeLog.new_recorder_serial_number
                      "
                    >
                      <td>Recorder Serial Number</td>
                      <td>{{ changeLog.old_recorder_serial_number }}</td>
                      <td>{{ changeLog.new_recorder_serial_number }}</td>
                    </tr>
                    <tr
                      v-if="
                        JSON.stringify(changeLog.old_reason_tags) !==
                          JSON.stringify(changeLog.new_reason_tags)
                      "
                    >
                      <td>Reason For Download</td>
                      <td>
                        <ul
                          v-if="changeLog.old_reason_tags"
                          class="margin-0"
                        >
                          <li
                            v-for="reason in changeLog.old_reason_tags"
                            :key="reason"
                          >
                            {{ reason }}
                          </li>
                        </ul>
                      </td>
                      <td>
                        <ul
                          v-if="changeLog.new_reason_tags"
                          class="margin-0"
                        >
                          <li
                            v-for="reason in changeLog.new_reason_tags"
                            :key="reason"
                          >
                            {{ reason }}
                          </li>
                        </ul>
                      </td>
                    </tr>
                    <tr
                      v-if="
                        changeLog.old_purchase_order !==
                          changeLog.new_purchase_order
                      "
                    >
                      <td>Purchase Order</td>
                      <td>{{ changeLog.old_purchase_order }}</td>
                      <td>{{ changeLog.new_purchase_order }}</td>
                    </tr>
                    <tr
                      v-if="
                        changeLog.old_invoice_number !==
                          changeLog.new_invoice_number
                      "
                    >
                      <td>Invoice Number</td>
                      <td>{{ changeLog.old_invoice_number }}</td>
                      <td>{{ changeLog.new_invoice_number }}</td>
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>
          </tbody>
        </responsive-table>
      </div>
    </b-modal>
  </fragment>
</template>

<script>
import Loader from '@/components/Loader.vue';
import ResponsiveTable from '@/components/ResponsiveTable.vue';
import paginationHelper from '../../common/paginationHelper';

/* eslint-disable vue/require-default-prop */
export default {
  name: 'UsageTable',
  components: {
    ResponsiveTable,
    Loader,
  },
  props: {
    reportData: {
      type: Array,
    },
    downloadFile: {
      type: Function,
    },
    showEditModal: {
      type: Function,
    },
    getUsageChangeLog: {
      type: Function,
    },
  },
  emits: ['update-sort'],
  data: () => ({
    ascending: false,
    sortColumn: '',
    changeLogUsage: {},
    columns: [
      {
        id: 'created',
        display: 'Reported Time',
        class: 'date-col',
        format: (v) => {
          const d = new Date(v);
          return d.toLocaleString();
        },
        title: (v) => {
          const d = new Date(v);
          return d.toUTCString();
        },
      },
      {
        id: 'duration',
        display: 'Duration',
        format: (secondsDifference) => {
          const secondsInMinute = 60;
          const secondsInHour = 60 * secondsInMinute;
          let hours = '0';
          let minutes = '00';
          let seconds = '00';
          if (secondsDifference > secondsInHour) {
            hours = Math.floor(secondsDifference / secondsInHour);
          }
          if (secondsDifference > secondsInMinute) {
            minutes = `00${Math.floor(
              (secondsDifference - hours * secondsInHour) / secondsInMinute,
            )}`.slice(-2);
          }
          seconds = `00${secondsDifference
            - hours * secondsInHour
            - minutes * secondsInMinute}`.slice(-2);

          return `${hours}:${minutes}:${seconds}`;
        },
        title: (_, record) => {
          const startTime = new Date(record.startTime);

          const stopTime = new Date(record.stopTime);
          return `${startTime.toUTCString()} - ${stopTime.toUTCString()}`;
        },
      },
      {
        id: 'serialNumber',
        display: 'DART SN',
      },
      {
        id: 'organizationName',
        display: 'Organization',
        adminOnly: true,
      },
      {
        id: 'siteName',
        display: 'Site',
      },
      {
        id: 'recorderTypeName',
        class: 'recorder-type',
        display: 'Recorder Type',
      },
      {
        id: 'usageType',
        display: 'Usage Type',
        style: 'text-transform:capitalize;',
      },
      {
        id: 'tailNumber',
        display: 'Tail #',
      },
      {
        id: 'filename',
        display: 'Filename',
        isDownloadLink: true,
      },
      {
        id: 'recorderPartNumber',
        display: 'Recorder Part Number',
      },
      {
        id: 'recorderSerialNumber',
        display: 'Recorder Serial Number',
      },
      {
        id: 'recorderTags',
        display: 'Reason For Download',
        isBulletedList: true,
      },
      {
        id: 'database',
        display: 'Database',
        class: 'no-wrap',
      },
      {
        id: 'ownership',
        display: 'Ownership',
        style: 'text-transform:capitalize;',
        adminOnly: true,
      },
      {
        id: 'purchaseOrder',
        display: 'Purchase Order',
      },
      {
        id: 'invoiceNumber',
        display: 'Invoice Number',
        adminOnly: true,
      },
    ],
    currentPage: 1,
    recordsPerPage: 50,
    loadingChangeLog: false,
    changeLogData: [],
  }),
  computed: {
    filteredReportData() {
      return (
        this.sortedData().slice(
          (this.currentPage - 1) * this.recordsPerPage,
          this.currentPage * this.recordsPerPage,
        ) || []
      );
    },
    displayColumns() {
      return this.columns.filter(
        x => !x.adminOnly
          || (x.adminOnly && this.$store.getters.isKgbAdminUser),
      );
    },
  },
  watch: {
    // eslint-disable-next-line func-names
    '$route.params': function () {
      this.handleRouteParams(this.$route.query);
    },
  },
  mounted() {
    this.handleRouteParams(this.$route.query);
  },
  methods: {
    handleRouteParams(queryParameterObject) {
      this.sortColumn = queryParameterObject.o;
      this.ascending = !!(
        queryParameterObject.a && queryParameterObject.a.toString() === 'true'
      );
      this.currentPage = queryParameterObject.p || 1;
    },
    pageChange(page) {
      this.updateRoute(page, this.sortColumn, this.ascending);
    },
    updateRoute(page, column, ascending) {
      this.$router.push({
        query: {
          ...this.$route.query,
          ...paginationHelper(page, column, ascending),
        },
      });
    },
    sortedData() {
      if (!this.sortColumn) {
        return this.reportData;
      }
      return [...this.reportData].sort((a, b) => {
        if (a[this.sortColumn] === null) {
          return this.ascending ? 1 : -1;
        }
        if (b[this.sortColumn] === null) {
          return this.ascending ? -1 : 1;
        }
        if (a[this.sortColumn] > b[this.sortColumn]) {
          return this.ascending ? 1 : -1;
        }
        if (a[this.sortColumn] < b[this.sortColumn]) {
          return this.ascending ? -1 : 1;
        }
        return 0;
      });
    },
    sortTable: function sortTable(col) {
      if (this.sortColumn === col) {
        this.ascending = !this.ascending;
      } else {
        this.ascending = true;
        this.sortColumn = col;
      }
      this.updateRoute(this.currentPage, this.sortColumn, this.ascending);
      this.$emit('update-sort', {
        col: this.sortColumn,
        asc: `${this.ascending}`,
      });
    },
    buildDeletedByText(usageData) {
      return `Deleted by ${usageData.fileDeletedBy} on ${usageData.fileDeletedDate}`;
    },
    goToEdit(record) {
      this.showEditModal(record);
    },
    goToHistory(record) {
      this.changeLogUsage = record;

      this.loadingChangeLog = true;
      this.changeLogData = [];
      this.$refs.usageHistoryModal.show();
      this.getUsageChangeLog(record.id).then((usageLog) => {
        this.loadingChangeLog = false;
        this.changeLogData = usageLog;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.date-col {
  min-width: 214px;
}

.recorder-type {
  min-width: 260px;
}

.deleted-file {
  color: $red;
}

.no-wrap {
  white-space: nowrap;
}
</style>
